/* eslint-disable tailwindcss/no-custom-classname */
import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getWorkoutCategories, updateWorkout } from "../../services/workouts";
import { toast } from "react-toastify";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select as FlowbiteSelect,
  TextInput,
  Textarea,
} from "flowbite-react";
import { difficulty, rounds } from "./data.json";
import { HiPlus } from "react-icons/hi";
import FileUploadZone from "../media/FileUploadZone";
import Select, { MultiValue } from "react-select";

export enum IntensityLevel {
  X1 = "X1",
  X2 = "X2",
  X3 = "X3",
  X4 = "X4",
  X5 = "X5",
  X6 = "X6",
  X7 = "X7",
}

const EditWorkoutModal = function (props: any) {
  const [isOpen, setOpen] = useState(false);
  const [update, setUpdate] = useState(false);

  const [updatedWorkout, setUpdatedWorkout]: any = useState({});

  // Array of touched fields
  const [touchedFields, setTouchedFields] = useState<string[]>([]);

  const rootRef = useRef<HTMLDivElement>(null);

  const [categories, setCategories] = useState([]);
  const [image, setImage]: any = useState(null);
  const [imageName, setImageName]: any = useState(null);
  const [imageType, setImageType]: any = useState(null);

  type CategoryOption = { value: string; label: string };

  const intensityArray = [
    { id: "Auto Assign", title: "Auto Assign" },
    ...Object.entries(IntensityLevel).map(([key, value]) => ({
      id: value,
      title: key,
    })),
  ];

  const handleChange = (e: any) => {
    const { name, checked, value } = e.target;
    const newValue =
      name === "published" || name === "featured" || name === "is_exclusive"
        ? checked
        : value;

    setUpdatedWorkout((prev: any) => ({
      ...prev,
      [name]: newValue,
    }));

    // Mark field as touched if not already
    if (!touchedFields.includes(name)) {
      setTouchedFields((prev) => [...prev, name]);
    }
  };

  const categoryOptions = useMemo(
    () =>
      categories.map((category: any) => ({
        value: category.id,
        label: category.name,
      })),
    [categories],
  );

  const initialCategoryIds = useMemo(() => {
    if (
      Array.isArray(props.workout?.categories) &&
      props.workout.categories.length > 0
    ) {
      return props.workout.categories.map((category: any) => category.id);
    }

    if (props.workout?.category_id != null) {
      return [props.workout.category_id];
    }

    return [];
  }, [props.workout]);

  const selectedCategoryIds = useMemo(() => {
    if (Array.isArray(updatedWorkout.categories)) {
      return updatedWorkout.categories;
    }

    return initialCategoryIds;
  }, [initialCategoryIds, updatedWorkout.categories]);

  const selectedCategoryOptions = useMemo(() => {
    if (!selectedCategoryIds?.length) {
      return [];
    }

    const categoryIdSet = new Set(selectedCategoryIds);

    return categoryOptions.filter((option) => categoryIdSet.has(option.value));
  }, [categoryOptions, selectedCategoryIds]);

  const handleCategoriesChange = (
    selectedOptions: MultiValue<CategoryOption>,
  ) => {
    setUpdatedWorkout((prev: any) => ({
      ...prev,
      categories: selectedOptions.map((option) => option.value),
    }));

    if (!touchedFields.includes("categories")) {
      setTouchedFields((prev) => [...prev, "categories"]);
    }
  };

  const handleSubmit = async () => {
    setUpdate(true);

    // Build an object with only touched fields
    const changedData = touchedFields.reduce(
      (acc, field) => {
        acc[field] = updatedWorkout[field];
        return acc;
      },
      {} as Record<string, any>,
    );

    if (changedData["intensity"] === "Auto Assign") {
      delete changedData["intensity"];
    }

    const payload = {
      ...changedData,
      ...(image ? { cover_image: image } : {}),
      ...(imageName ? { image_name: imageName } : {}),
      ...(imageType ? { image_type: imageType } : {}),
    };

    const update_workout = await updateWorkout(payload, props.workout.id);

    if (update_workout.data.error) {
      toast.error(update_workout.data.message);
      Sentry.captureMessage("Error at updating a workout", {
        extra: {
          id: props.workout.id,
          payload: payload,
        },
      });
    } else {
      props.getWorkouts();
      setOpen(false);
      toast.success("Workout updated successfully!");
    }

    setUpdate(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setUpdatedWorkout({});
      setTouchedFields([]);
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await getWorkoutCategories();
      setCategories(response.data);
    } catch {
      toast.error("Failed to load workout categories");
      Sentry.captureMessage("Failed to load workout categories");
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  return (
    <>
      <div ref={rootRef}>
        <Button color="primary" onClick={() => setOpen(true)}>
          <div className="flex items-center gap-x-3">
            <HiPlus className="text-xl" />
            Edit Workout
          </div>
        </Button>
        <Modal
          onClose={() => setOpen(false)}
          show={isOpen}
          size="3xl"
          position="top-center"
          root={rootRef.current ?? undefined}
        >
          <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
            <strong>Edit Workout - {props.workout.title}</strong>
          </ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <Label htmlFor="firstName">Title</Label>
                <div className="mt-1">
                  <TextInput
                    id="title"
                    name="title"
                    value={updatedWorkout.title ?? props.workout.title}
                    placeholder="Title"
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lastName">Description</Label>
                <div className="mt-1">
                  <Textarea
                    id="description"
                    name="description"
                    value={
                      updatedWorkout.description ?? props.workout.description
                    }
                    placeholder="description"
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <div className="mt-1">
                  <Select
                    isMulti
                    options={categoryOptions}
                    value={selectedCategoryOptions}
                    onChange={handleCategoriesChange}
                    placeholder="Search and select categories..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => "No categories found"}
                  />
                </div>
              </div>

              <div>
                <Label>Difficulty</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={
                      updatedWorkout.difficulty ?? props.workout.difficulty
                    }
                    onChange={handleChange}
                    name="difficulty"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {difficulty.map((x: any, i: number) => (
                      <option key={i} value={i}>
                        {x}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Select Trainer</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={updatedWorkout.trainer ?? props.workout.trainer_id}
                    onChange={handleChange}
                    name="trainer"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {props.trainers.map((trainer: any, i: any) => (
                      <option key={i} value={trainer.id}>
                        {trainer.title}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Select Intensity</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={updatedWorkout.intensity ?? props.workout.intensity}
                    onChange={handleChange}
                    name="intensity"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {intensityArray.map((item, i) => (
                      <option key={i} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Rounds</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={updatedWorkout.rounds ?? props.workout.rounds}
                    name="rounds"
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {rounds.map((x: any, i: number) => (
                      <option key={i} value={i + 1}>
                        {x}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              {/* Empty space */}
              <div></div>

              <div>
                <Label>Cover Image</Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpg",
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                  ]}
                  onImageChange={(image) => {
                    setImage(image.dataUri);
                    setImageName(image.name);
                    setImageType(image.type);
                  }}
                  defaultPreview={
                    props?.workout?.cover_image
                      ? {
                          name: props?.workout?.cover_image,
                          url: props?.workout?.cover_image,
                          type: "image/*",
                        }
                      : undefined
                  }
                  maxDimensions={{ width: 2000, height: 2000 }}
                  maxSize={10 * 1024 * 1024}
                />
              </div>
            </div>

            <div className="mt-12 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-6 text-sm font-semibold text-gray-900 dark:text-white">
                Visibility & Status
              </h3>
              <div className="flex flex-col space-y-4">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    name="published"
                    checked={
                      updatedWorkout.published ?? props.workout.published
                    }
                    className="peer sr-only"
                    onChange={handleChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Published
                  </span>
                </label>

                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={updatedWorkout.featured ?? props.workout.featured}
                    className="peer sr-only"
                    onChange={handleChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Featured
                  </span>
                </label>

                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    name="is_exclusive"
                    checked={
                      updatedWorkout.is_exclusive ??
                      props.workout.is_exclusive ??
                      false
                    }
                    className="peer sr-only"
                    onChange={handleChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Exclusive (Requires Subscription)
                  </span>
                </label>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleSubmit} disabled={update}>
              {update ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default EditWorkoutModal;
